import {
  DacDocSubmissionRepository,
  DacDocSubmissionRepositoryFactory,
  DacDocSubmissionStore,
} from '@/store/dac-doc-submission/dac-doc-submission-state.repository';
import { Component, inject, Input, OnInit } from '@angular/core';
import { getDacSubmissionPiecesFormObject } from './utils/dac-submission-pieces.form-object';
import { FormBuilderComponent } from '@/components/form-builder/form-builder.component';
import { DacService } from '../dac.service';
import { MessageService } from 'primeng/api';
import { getFormBuilderObjectFactory } from '@/components/form-builder/utils/form-builder-factory';
import { BehaviorSubject, combineLatest, forkJoin, map, Observable, of } from 'rxjs';
import { SelectOption } from '@/components/form-builder/form-builder.types';
import { DocSubmission, WithMetadata } from '@/types';
import { MultiFormAccordionService } from '@/components/multi-form-accordion/multi-form-accordion.service';
import { DacDocSubmissionDto } from '@/types';
import { startWith } from 'rxjs/operators';

@Component({
  selector: 'sm-dac-submission-pieces',
  imports: [FormBuilderComponent],
  templateUrl: './dac-submission-pieces.component.html',
  styleUrl: './dac-submission-pieces.component.scss',
})
export class DacSubmissionPiecesComponent implements OnInit {
  dacService = inject(DacService);
  dacDocSubmissionRepository?: DacDocSubmissionRepository;
  messageService = inject(MessageService);

  @Input() dacId?: number;
  @Input() dacVer?: number;
  @Input() store?: DacDocSubmissionStore;
  @Input() docTypeId?: number;
  @Input() isDisabled: Observable<boolean> = of(false);
  @Input() usedDocSubmissionIds: Observable<number[]> = of([]);
  @Input() accordion?: MultiFormAccordionService;

  loading$ = new BehaviorSubject<boolean>(true);

  formObject = getFormBuilderObjectFactory();

  rawDocOptions: BehaviorSubject<WithMetadata<DocSubmission>[]> = new BehaviorSubject<WithMetadata<DocSubmission>[]>([]);
  docOptions: BehaviorSubject<SelectOption[]> = new BehaviorSubject<SelectOption[]>([]);
  validationDateRequired: Observable<boolean> = of(true);

  ngOnInit(): void {
    this.buildFormObject();
  }

  loadFormOptions(type?: number) {
    if (!this.dacId || !this.dacVer || !type) return;

    forkJoin([
      this.dacService.getDocSubmissionNotInDacByType(this.dacId, type),
      this.dacService.getDocSubmissionInDacByType(this.dacId, type),
    ]).subscribe(([notInDac, inDac]) => {
      const response = [...notInDac, ...inDac];

      this.rawDocOptions.next(response);

      const selected = this.dacDocSubmissionRepository?.get<SelectOption>('docSubmissionNotInDacSelected');

      const items: SelectOption[] = response.map(uf => ({
        name: uf.nom ?? '',
        code: uf.id,
      }));

      if (selected?.code && !items.some(i => i.code === selected.code)) {
        items.push({
          name: selected.name ?? '',
          code: selected.code as number,
        });
      }

      this.docOptions.next(items);
    });
  }
  private buildFormObject() {
    if (this.store && this.docTypeId) {
      this.dacDocSubmissionRepository = DacDocSubmissionRepositoryFactory.create(this.store);
      //load form options for folder type
      this.loadFormOptions(this.docTypeId);

      // Combine used IDs from other tabs with IDs from sibling pieces in the same accordion
      const allUsedIds$ = this.createAllUsedIdsObservable();

      //build form object
      this.formObject = getDacSubmissionPiecesFormObject({
        store: this.dacDocSubmissionRepository as DacDocSubmissionRepository,
        className: 'p-5',
        rawDocOptions: this.rawDocOptions,
        docOptions: this.docOptions,
        loadDocOptions: (type: number) => this.loadFormOptions(type),
        isDisabled: this.isDisabled,
        usedDocSubmissionIds: allUsedIds$,
      });
    }
  }

  private createAllUsedIdsObservable(): Observable<number[]> {
    if (!this.accordion) {
      return this.usedDocSubmissionIds;
    }

    // Get observable for sibling pieces in the same accordion
    const siblingIds$ = new Observable<number[]>(observer => {
      const emitSiblingIds = () => {
        const stores = this.accordion!.getAllStores();
        const siblingIds = stores
          .filter(s => s !== this.store) // Exclude current store
          .map(store => {
            const repo = DacDocSubmissionRepositoryFactory.create(store);
            return repo.get<DacDocSubmissionDto>('dto')?.docSoumissionId;
          })
          .filter((id): id is number => id !== undefined && id !== null);
        observer.next(siblingIds);
      };

      emitSiblingIds();

      // Listen for changes to accordion stores
      const newStoreSub = this.accordion!.newStore$.subscribe(() => emitSiblingIds());
      const removeStoreSub = this.accordion!.storeRemoved$.subscribe(() => emitSiblingIds());

      return () => {
        newStoreSub.unsubscribe();
        removeStoreSub.unsubscribe();
      };
    }).pipe(startWith([]));

    // Combine with other tabs' used IDs
    return combineLatest([this.usedDocSubmissionIds, siblingIds$]).pipe(map(([otherTabsIds, siblingIds]) => [...otherTabsIds, ...siblingIds]));
  }
}
